import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { AutocompleteRenderOptionState } from '@material-ui/lab';
import { Cancel, Done } from '@material-ui/icons';
import { AxiosError } from 'axios';
import * as yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';
import { getRandomExecutor, upload } from 'api';
import AsyncSearch from 'components/AsyncSearch';
import useError from 'components/ErrorProvider/useError';
import FilesList from 'components/FilesList';
import VirtualizedListbox from 'components/VirtualizedListbox';
import { IDefectModel, IExecutorModel, IRealEstateModel } from 'interfaces';
import { getDefectType, getRealEstate, getRealEstatesShort } from 'pages/PageApplicationNew/api';
import { getApplicationAccidentDefectsByPositionShort, saveApplicationAccidentV2 } from './api';
import { IApplicationAccident, IApplicationAccidentDefectType } from './interfaces';

const useStyles = makeStyles({
  table: {
    '& thead': {
      '& th': {
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          backgroundColor: '#FAFAFA',
        },
      },
    },
  },
});

interface IProps {
  realEstateGuid?: string;
  showRealEstateFormControl?: boolean;
  onClose(): void;
  onSave(): void;
}

const ApplicationAccidentDialog = ({
  realEstateGuid,
  showRealEstateFormControl,
  onClose,
  onSave,
  ...props
}: IProps & DialogProps) => {
  const validationSchema = yup.object().shape({
    defectType: yup.object().nullable().required('Выберите тип замечания'),
    ...(showRealEstateFormControl && {
      realEstates: yup
        .array()
        .of(yup.object().nullable().required('Обязательное поле'))
        .min(1, 'Выберите как минимум один объект недвижимости'),
    }),
    comment: yup.string().required('Введите комментарий'),
    photoList: yup.array().of(yup.string()),
  });

  // Local state
  const [defectType, setDefectType] = useState<IDefectModel | null>(null);
  const [defectTypeLoading, setDefectTypeLoading] = useState(false);

  const [executorsMap, setExecutorsMap] = useState<Map<string, IExecutorModel>>(new Map());
  const [executorsLoading, setExecutorsLoading] = useState(false);

  const [disabled, setDisabled] = useState(false);

  // Global notification
  const { addError } = useError();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues: IApplicationAccident = {
    defectType: null,
    realEstates: [],
    comment: '',
    photoList: [],
  };

  async function fetchExecutorsForRealEstates(guids: string[], defectType: IApplicationAccidentDefectType) {
    try {
      setExecutorsLoading(true);

      const results = await Promise.all(
        guids.map(async (guid) => {
          const executor = await getRandomExecutor(guid, defectType.defectTypeId);
          return { guid, executor };
        }),
      );

      setExecutorsMap((prev) => {
        const next = new Map(prev);
        results.forEach(({ guid, executor }) => next.set(guid, executor));
        return next;
      });
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setExecutorsLoading(false);
    }
  }

  async function fetchDefectType(defectType: IApplicationAccidentDefectType) {
    try {
      setDefectType(null);
      setDefectTypeLoading(true);

      setDefectType(await getDefectType(defectType.defectTypeId));
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDefectTypeLoading(false);
    }
  }

  async function handleSubmit(values: IApplicationAccident) {
    const selectedGuids = (values.realEstates || []).map((re) => re.guid);
    const fallbackGuids = realEstateGuid ? [realEstateGuid] : [];

    // Если поле выбора ЖК скрыто — используем realEstateGuid из пропсов
    const realEstateGuids = selectedGuids.length > 0 ? selectedGuids : fallbackGuids;

    if (realEstateGuids.length === 0) return addError('Выберите как минимум один объект недвижимости');

    try {
      setDisabled(true);
      const { realEstates, defectType, ...props } = values;

      const photoList = await upload(values.photoList as File[]);

      await saveApplicationAccidentV2({
        realEstateGuids,
        defectTypeId: defectType?.defectTypeId,
        comment: props.comment,
        photoList,
      });
      onClose();

      onSave();
      enqueueSnackbar(
        <Typography>
          {realEstateGuids.length > 1
            ? `Аварии были успешно зарегистрированы (${realEstateGuids.length})`
            : 'Авария была успешно зарегистрирована'}
        </Typography>,
        { variant: 'success' },
      );
    } catch (error) {
      addError((error as AxiosError).response?.data?.message);
    } finally {
      setDisabled(false);
    }
  }

  return (
    <Dialog {...props} onClose={onClose} maxWidth="lg" fullWidth scroll="body">
      <DialogTitle>
        <b>Зарегистрировать аварию</b>
      </DialogTitle>

      <Formik
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ values, setFieldValue }) => {
          const realEstateGuids = (values.realEstates || []).map((re) => re.guid);
          const allGuids = realEstateGuids.length > 0 ? realEstateGuids : realEstateGuid ? [realEstateGuid] : [];

          function handleChangeDefectType(defectType: IApplicationAccidentDefectType) {
            setFieldValue('defectType', defectType);

            fetchDefectType(defectType);
            if (allGuids.length > 0) fetchExecutorsForRealEstates(allGuids, defectType);
          }

          function handleChangeRealEstates(realEstates: IRealEstateModel[]) {
            setFieldValue('realEstates', realEstates);

            if (values.defectType) {
              const newGuids = realEstates
                .map((re) => re.guid)
                .filter((guid) => !executorsMap.has(guid));

              if (newGuids.length > 0) fetchExecutorsForRealEstates(newGuids, values.defectType);
            }
          }

          return (
            <Form>
              <DialogContent dividers>
                <ApplicationAccidentFormControls
                  showRealEstateFormControl={showRealEstateFormControl}
                  realEstateGuid={realEstateGuid}
                  defectType={defectType}
                  executorsMap={executorsMap}
                  disabled={disabled}
                  loading={defectTypeLoading || executorsLoading}
                  onChangeDefectType={handleChangeDefectType}
                  onChangeRealEstates={handleChangeRealEstates}
                />
              </DialogContent>

              <DialogActions>
                <Button variant="contained" disabled={disabled} onClick={onClose} startIcon={<Cancel />}>
                  Отменить
                </Button>

                <Button color="primary" variant="contained" disabled={disabled} type="submit" startIcon={<Done />}>
                  Зарегистрировать
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

const ApplicationAccidentFormControls = ({
  defectType,
  executorsMap,
  disabled,
  loading,
  realEstateGuid,
  showRealEstateFormControl,
  onChangeDefectType,
  onChangeRealEstates,
}: {
  defectType: IDefectModel | null;
  executorsMap: Map<string, IExecutorModel>;
  disabled: boolean;
  loading: boolean;
  onChangeDefectType(defectType: IApplicationAccidentDefectType): void;
  onChangeRealEstates(realEstates: IRealEstateModel[]): void;
} & Omit<IProps, 'onClose' | 'onSave'>) => {
  const { values, touched, errors, setFieldValue, handleChange } = useFormikContext<IApplicationAccident>();

  // Styles
  const classes = useStyles();

  // Предзагрузка текущего ЖК в мультиселект
  useEffect(() => {
    if (showRealEstateFormControl && realEstateGuid) {
      getRealEstate(realEstateGuid).then((realEstate) => {
        if (realEstate) setFieldValue('realEstates', [realEstate]);
      });
    }
  }, [realEstateGuid]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChangeDefectType(defectType: IApplicationAccidentDefectType) {
    setFieldValue('defectType', defectType);
    onChangeDefectType(defectType);
  }

  const renderRealEstateOption = (realEstate: IRealEstateModel, state: AutocompleteRenderOptionState) => (
    <Box sx={{ display: 'flex', gridGap: 8 }}>
      <Checkbox checked={state.selected} color="primary" />

      <Box>
        <Typography style={{ margin: 0 }}>{realEstate.name}</Typography>

        {realEstate.addressRu && realEstate.addressRu.length > 0 && (
          <Typography style={{ margin: 0, color: ' rgba(0, 0, 0, 0.5)' }}>
            <small>{realEstate.addressRu}</small>
          </Typography>
        )}
      </Box>
    </Box>
  );

  const fetchRealEstates = useCallback(
    () =>
      getRealEstatesShort({
        onlyFromMatrix: true,
        onlyWithKeytransferDate: true,
        isNoneService: true,
        includeUnserved: false,
        nameWithCity: true,
        searchWithCity: false,
        isAccident: true,
      }),
    [],
  );

  return (
    <Grid container spacing={2}>
      {showRealEstateFormControl && (
        <Grid item xs={12}>
          <AsyncSearch<IRealEstateModel, true>
            value={values.realEstates || []}
            multiple
            fetch={fetchRealEstates}
            uniqueBy="guid"
            matchKeys={['name', 'addressRu']}
            disabled={disabled}
            disableCloseOnSelect
            noOptionsText="Объекты недвижимости отсутствуют"
            getOptionLabel={(option) => option.name || ''}
            onChange={(event, options) => onChangeRealEstates(options)}
            ListboxComponent={VirtualizedListbox}
            renderOption={renderRealEstateOption}
            renderInput={(params) => (
              <TextField
                label="Объекты недвижимости"
                variant="filled"
                error={Boolean(touched.realEstates) && Boolean(errors.realEstates)}
                helperText={Boolean(touched.realEstates) && errors.realEstates}
                {...params}
              />
            )}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <AsyncSearch
          value={values.defectType}
          fetch={getApplicationAccidentDefectsByPositionShort}
          matchKeys={['defectName']}
          uniqueBy="defectTypeId"
          onChange={(event, option) => option && handleChangeDefectType(option)}
          disabled={disabled}
          getOptionLabel={(option) => String(option.defectName)}
          getOptionSelected={(option, value) => option.defectTypeId === value.defectTypeId}
          renderInput={(params) => (
            <TextField
              {...params}
              name="defectType"
              variant="filled"
              label="Тип замечания"
              placeholder="Выберите из списка"
              fullWidth
              error={Boolean(touched.defectType && errors.defectType)}
              helperText={touched.defectType && errors.defectType}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper} variant="outlined">
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                {showRealEstateFormControl && (
                  <TableCell>
                    <b>ЖК</b>
                  </TableCell>
                )}

                <TableCell>
                  <b>Вид работ</b>
                </TableCell>

                <TableCell>
                  <b>Исполнитель</b>
                </TableCell>

                <TableCell>
                  <b>Должность</b>
                </TableCell>

                <TableCell>
                  <b>Срок исполнения (часы)</b>
                </TableCell>

                <TableCell>
                  <b>Прайс</b>
                </TableCell>

                <TableCell>
                  <b>Срок гарантии (мес.)</b>
                </TableCell>

                {defectType?.ovksFine !== null && (
                  <TableCell>
                    <b>Сумма штрафа (мрп)</b>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={showRealEstateFormControl ? 8 : 7}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                defectType &&
                executorsMap.size > 0 &&
                Array.from(executorsMap.entries()).map(([guid, executor]) => (
                  <TableRow key={guid}>
                    {showRealEstateFormControl && (
                      <TableCell>
                        {(values.realEstates || []).find((re) => re.guid === guid)?.name || guid}
                      </TableCell>
                    )}

                    <TableCell>{defectType.workTypeName}</TableCell>

                    <TableCell>
                      {executor.userId ? `${executor.firstName} ${executor.lastName}` : 'Не указан'}
                    </TableCell>

                    <TableCell>{executor.positionName || ''}</TableCell>

                    <TableCell>
                      {defectType.taskDeadlineMin && defectType.taskDeadlineMax
                        ? `${defectType.taskDeadlineMin} - ${defectType.taskDeadlineMax}`
                        : defectType.taskDeadline}
                    </TableCell>

                    <TableCell>{defectType.cost}</TableCell>

                    <TableCell>{defectType.warrantyDeadLine}</TableCell>

                    {defectType.ovksFine !== null && <TableCell>{defectType.ovksFine}</TableCell>}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12}>
        <TextField
          name="comment"
          variant="filled"
          label="Комментарий"
          placeholder="Введите комментарий"
          fullWidth
          multiline
          minRows={4}
          disabled={disabled}
          value={values.comment}
          onChange={handleChange}
          error={Boolean(touched.comment && errors.comment)}
          helperText={touched.comment && errors.comment}
        />
      </Grid>

      <Grid item xs={12}>
        <FilesList
          files={values.photoList || []}
          editable={!disabled}
          width={90}
          height={90}
          onChange={(files) => setFieldValue('photoList', files)}
        />
      </Grid>
    </Grid>
  );
};

export default ApplicationAccidentDialog;
